import React, { useState } from 'react';
import { NodeProps } from '@xyflow/react';
import useLocale from 'next-translate/useTranslation';
import { BaseNode, createOutputHandle } from './BaseNode';
import { useWorkflowStore } from '@/lib/workflows/store';
import { createClient as createComponentClient } from '@/utils/supabase/component';
import { toast } from 'sonner';
import uuidv4 from '@/lib/uuid';
import { compressImage } from '@/utils/compressImage';
import Spinner from '@/components/Spinner';
import { XIcon, PhotographIcon } from '@heroicons/react/outline';
import clsxm from '@/lib/clsxm';
import { useIsMobile } from '@/hooks/useIsMobile';

interface UploadNodeProps extends NodeProps {
  nodeId?: string;
  readOnly?: boolean;
}

export function UploadNode({
  id,
  data,
  selected,
  nodeId,
  readOnly,
}: UploadNodeProps) {
  const { t } = useLocale('common');
  const isMobile = useIsMobile();
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const addNode = useWorkflowStore((state) => state.addNode);
  const allNodes = useWorkflowStore((state) => state.nodes);
  const supabaseClient = createComponentClient();
  const [isUploading, setIsUploading] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const config = (data.config || {}) as {
    imageUrl?: string;
    videoUrl?: string;
    audioUrl?: string;
    mediaType?: 'image' | 'video' | 'audio';
  };
  const mediaType =
    config.mediaType ||
    (config.videoUrl ? 'video' : config.audioUrl ? 'audio' : 'image');
  const imageUrl = config.imageUrl || '';
  const videoUrl = config.videoUrl || '';
  const audioUrl = config.audioUrl || '';

  const uploadSingleFile = async (
    rawFile: File,
  ): Promise<{ mediaType: 'image' | 'video' | 'audio'; url: string }> => {
    const isImage = rawFile.type.startsWith('image/');
    const isVideo = rawFile.type.startsWith('video/');
    const isAudio = rawFile.type.startsWith('audio/');

    if (!isImage && !isVideo && !isAudio) {
      throw new Error(t('Unsupported file type'));
    }

    // Downscale/re-encode oversized images instead of rejecting them, so
    // users can upload straight from a phone or camera. compressImage caps
    // the long edge at 3840px (under the 4000px limit) and re-encodes to
    // JPEG (universally accepted by downstream AI models); `force` makes it
    // run regardless of the source file size.
    const file = isImage
      ? await compressImage(rawFile, { force: true, format: 'jpeg' })
      : rawFile;

    const maxImage = 10 * 1024 * 1024; // 10MB
    const maxVideo = 50 * 1024 * 1024; // 50MB
    const maxAudio = 20 * 1024 * 1024; // 20MB
    const maxFileSize = isImage ? maxImage : isVideo ? maxVideo : maxAudio;
    if (file.size > maxFileSize) {
      throw new Error(
        t(
          'File size exceeds the maximum allowed size of {{size}}MB. Please upload a smaller file.',
          {
            size: (maxFileSize / 1024 / 1024).toFixed(0),
          },
        ),
      );
    }

    // Validate dimensions of the (already compressed) image. JPEG/PNG are
    // downscaled to <= 3840px above, so the max check below is now a
    // fallback for formats compressImage leaves untouched (WebP/AVIF/GIF).
    if (isImage) {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      try {
        await new Promise((resolve, reject) => {
          img.onload = () => {
            if (img.width < 512 || img.height < 512) {
              reject(
                new Error(
                  t(
                    'Image must be at least 512x512 pixels. Your image is {{width}}x{{height}} pixels.',
                    { width: img.width, height: img.height },
                  ),
                ),
              );
            } else if (img.width > 4000 || img.height > 4000) {
              reject(
                new Error(
                  t(
                    'Image exceeds the maximum allowed dimensions of 4000px ({{width}}x{{height}})',
                    { width: img.width, height: img.height },
                  ),
                ),
              );
            } else {
              resolve(true);
            }
          };
          img.onerror = () => reject(new Error(t('Image loading failed')));
        });
      } finally {
        URL.revokeObjectURL(img.src);
      }
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `workflows/${fileName}`;

    const { error: uploadError } = await supabaseClient.storage
      .from('uploads')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw new Error(t('Error uploading image'));
    }

    const { data: fileData } = await supabaseClient.storage
      .from('uploads')
      .getPublicUrl(filePath);

    if (!fileData?.publicUrl) {
      throw new Error(t('Error getting file URL'));
    }

    return {
      mediaType: isImage ? 'image' : isVideo ? 'video' : 'audio',
      url: fileData.publicUrl,
    };
  };

  const buildConfig = (
    mediaType: 'image' | 'video' | 'audio',
    url: string,
  ) => ({
    mediaType,
    imageUrl: mediaType === 'image' ? url : '',
    videoUrl: mediaType === 'video' ? url : '',
    audioUrl: mediaType === 'audio' ? url : '',
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Reset so re-selecting the same file re-triggers onChange.
    e.target.value = '';
    if (files.length === 0) return;

    setIsUploading(true);

    const results = await Promise.allSettled(files.map(uploadSingleFile));
    const successes: Array<{
      mediaType: 'image' | 'video' | 'audio';
      url: string;
    }> = [];
    results.forEach((r, idx) => {
      if (r.status === 'fulfilled') {
        successes.push(r.value);
      } else {
        console.error('File upload error:', r.reason);
        const msg = r.reason?.message || t('Error uploading file');
        toast.error(`${files[idx].name}: ${msg}`);
      }
    });

    if (successes.length === 0) {
      setIsUploading(false);
      return;
    }

    // First successful file goes into the current node; remaining files spawn
    // sibling Upload Media nodes cascaded to the right.
    const [first, ...rest] = successes;
    updateNodeData(id, {
      config: buildConfig(first.mediaType, first.url),
      result: first.url,
    });

    if (rest.length > 0) {
      const parentNode = allNodes.find((n) => n.id === id);
      const baseX = parentNode?.position.x ?? 0;
      const baseY = parentNode?.position.y ?? 0;
      const stepX = 460;

      rest.forEach((res, i) => {
        addNode({
          id: `upload-${Date.now()}-${i + 1}`,
          type: 'upload',
          position: { x: baseX + stepX * (i + 1), y: baseY },
          data: {
            label: t('Upload Media'),
            type: 'upload',
            config: buildConfig(res.mediaType, res.url),
            result: res.url,
          },
        } as any);
      });

      toast.success(
        t('{{count}} files uploaded successfully', { count: successes.length }),
      );
    } else if (first.mediaType === 'image') {
      toast.success(t('Image uploaded successfully'));
    } else if (first.mediaType === 'video') {
      toast.success(t('Video uploaded successfully'));
    } else {
      toast.success(t('Audio uploaded successfully'));
    }

    setIsUploading(false);
  };

  const handleRemoveImage = () => {
    updateNodeData(id, {
      config: {
        mediaType: 'image',
        imageUrl: '',
        videoUrl: '',
        audioUrl: '',
      },
      result: '',
    });
  };

  return (
    <BaseNode
      title={(data.label as string) || t('Upload Media')}
      icon={
        <svg
          className='w-4 h-4 text-purple-600'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' />
        </svg>
      }
      hasInput={false}
      hasOutput={false}
      status={data.status as any}
      selected={selected}
      nodeId={nodeId || id}
      readOnly={readOnly}
      customOutputs={
        mediaType === 'video' ? (
          <>
            {createOutputHandle('image', 'first-frame', 30)}
            {createOutputHandle('image', 'last-frame', 60)}
            {createOutputHandle('video', 'video', 90)}
          </>
        ) : mediaType === 'audio' ? (
          createOutputHandle('audio', 'audio', 50)
        ) : (
          createOutputHandle('image', 'image', 50)
        )
      }
    >
      <div
        className={clsxm(
          imageUrl || videoUrl || audioUrl ? '' : 'space-y-3 px-4 py-3',
        )}
      >
        {/* Upload Button */}
        {!(imageUrl || videoUrl || audioUrl) && (
          <div>
            <label className='flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'>
              <div className='flex flex-col items-center justify-center pt-5 pb-6'>
                {isUploading ? (
                  <Spinner className='h-8 w-8 text-primary-500' />
                ) : (
                  <>
                    <PhotographIcon className='h-10 w-10 text-gray-400 dark:text-gray-500 mb-2' />
                    <p className='text-sm text-gray-600 dark:text-gray-300'>
                      {t('Click to upload media')}
                    </p>
                    <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                      PNG, JPG, WEBP, MP4, MOV, WEBM, MP3, WAV
                    </p>
                  </>
                )}
              </div>
              <input
                type='file'
                className='hidden'
                accept='image/*,video/*,audio/*'
                multiple
                onChange={handleFileUpload}
                disabled={isUploading || readOnly}
              />
            </label>
          </div>
        )}

        {/* Preview */}
        {imageUrl && mediaType === 'image' && (
          <div className={clsxm(isLandscape ? 'w-[700px]' : 'w-[420px]')}>
            <div className='relative group rounded-md overflow-hidden'>
              <img
                src={imageUrl}
                alt='Uploaded'
                className='w-full h-auto object-cover'
                onLoad={(e) => {
                  const img = e.currentTarget;
                  setIsLandscape(img.naturalWidth > img.naturalHeight);
                }}
              />
              <button
                onClick={handleRemoveImage}
                className='absolute right-2 top-2 rounded-full bg-red-500 p-2 shadow-lg hover:bg-red-600 transition-all'
                title={t('Remove file')}
              >
                <XIcon className='h-5 w-5 text-white' />
              </button>
            </div>
          </div>
        )}
        {videoUrl && mediaType === 'video' && (
          <div className={clsxm(isLandscape ? 'w-[700px]' : 'w-[420px]')}>
            <div className='relative group rounded-md overflow-hidden'>
              <video
                src={videoUrl}
                controls
                className='w-full h-auto object-cover'
                autoPlay={!isMobile}
                preload={isMobile ? 'metadata' : 'auto'}
                muted
                playsInline
                loop
                onLoadedMetadata={(e) => {
                  const video = e.currentTarget;
                  setIsLandscape(video.videoWidth > video.videoHeight);
                }}
              />
              <button
                onClick={handleRemoveImage}
                className='absolute right-2 top-2 rounded-full bg-red-500 p-2 shadow-lg hover:bg-red-600 transition-all'
                title={t('Remove file')}
              >
                <XIcon className='h-5 w-5 text-white' />
              </button>
            </div>
          </div>
        )}
        {audioUrl && mediaType === 'audio' && (
          <div className='relative'>
            <audio src={audioUrl} controls className='w-full' />
            <button
              onClick={handleRemoveImage}
              className='absolute right-2 top-2 rounded-full bg-red-500 p-2 shadow-lg hover:bg-red-600 transition-all'
              title='Remove file'
            >
              <XIcon className='h-5 w-5 text-white' />
            </button>
          </div>
        )}

        {/* Info */}
        {/* <div className='text-xs text-gray-500 bg-gray-50 p-2 rounded'>
          Upload an image to use in other nodes (edit, video generation, etc.)
        </div> */}
      </div>
    </BaseNode>
  );
}
